import React, { useContext, useEffect, useState } from "react";
import useStyles from "./styles";
import { UserContext } from "../../../UserProvider";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../store";
import { hasRole, UserLocation, UserOrder } from "../../../models/User";
import { permissions } from "../../../constants";
import { AlertTitle } from "@material-ui/lab";
import Alert from "@material-ui/lab/Alert";
import Progress from "../../../components/information/progress";
import { thunkFetchUserOrders } from "../../../store/users/actions";
import { StoreSelect } from "../../../components/selects/StoreSelect";
import { Store } from "../../../models/Store";
import { OrdersTable } from "./components/OrdersTable";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { State } from "./types";
import { useI18n } from "../../../hooks/useI18n";

export const UserOrders = () => {
  const { t } = useI18n();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const classes = useStyles();
  const dispatch = useDispatch();
  const [state, setState] = useState<State>({ type: "INITIAL" });
  const currentUser = useContext(UserContext);

  const userOrders =
    useSelector<AppState, ReadonlyArray<UserOrder>>(s => s.userOrders) ||
    undefined;

  const userLocation = useSelector<AppState, UserLocation>(s => s.userLocation);

  useEffect(() => {
    switch (state.type) {
      case "INITIAL":
        if (hasRole(currentUser, permissions.stores)) {
          if (params.storeId && params.storeId !== "any") {
            return setState({
              type: "LOAD_ORDERS",
              storeId: params.storeId
            });
          } else {
            return setState({ type: "STORE_SELECTION" });
          }
        }
        return setState({ type: "NOT_ALLOWED" });

      case "LOAD_ORDERS":
        dispatch(thunkFetchUserOrders());
        break;
    }
  }, [state.type]);

  useEffect(() => {
    switch (state.type) {
      case "ORDERS":
      case "STORE_SELECTION":
        if (params.storeId && params.storeId !== "any") {
          return setState({
            type: "LOAD_ORDERS",
            storeId: params.storeId
          });
        } else {
          return setState({ type: "STORE_SELECTION" });
        }
    }
  }, [location]);

  useEffect(() => {
    if (state.type !== "LOAD_ORDERS") {
      return;
    }

    const filtered = userOrders.filter(x => x.store.id === state.storeId);

    setState({
      type: "ORDERS",
      storeId: state.storeId,
      userOrders: filtered
    });
  }, [userOrders]);

  useEffect(() => {
    if (userLocation.storeId) {
      navigate(`/stores/${userLocation.storeId}/user-orders`);
    }
  }, [userLocation]);

  const handleStoreSelected = (store: Store) => {
    return navigate(`/stores/${store.id}/user-orders`);
  };

  switch (state.type) {
    case "NOT_ALLOWED":
      return (
        <Alert severity="warning">
          {t("common.messages.notAllowedToSeeThisView")}
        </Alert>
      );

    case "UNEXPECTED_ERROR":
      return (
        <Alert severity="error">
          <AlertTitle>
            {state.problem.status}, {state.problem.title}
          </AlertTitle>
          {state.problem.detail} <strong>{state.problem.traceId}</strong>
        </Alert>
      );

    case "INITIAL":
    case "LOAD_ORDERS":
      return (
        <div className={classes.loading}>
          <Progress isModule={true} />
        </div>
      );

    case "STORE_SELECTION":
      return (
        <>
          <Alert severity="info">
            Start by selecting which store you are in.
          </Alert>
          <StoreSelect
            className={classes.storeSelect}
            filterOpened={true}
            onSelected={handleStoreSelected}
          />
        </>
      );

    case "ORDERS":
      return <OrdersTable storeId={state.storeId} orders={state.userOrders} />;
  }
};
